import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Button, FormGroup, FormControl } from 'react-bootstrap';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
	isEqual, isEmpty, cloneDeep, findIndex, get,
} from 'lodash';
import utils, { constants } from '@utils';
import Actions, { thunks } from '@redux/app';
import { getCityStateZipString, updateProgressButton } from '@config/utils';
import { ProgressButton } from '@components';
import { Select } from '@components/common';
import FormField from './formField';

const SETTINGS_TYPE = 'personal';

class PersonalSettings extends Component {
	constructor() {
		super();

		this.state = {
			firstName : '',
			lastName  : '',
			email     : '',
			phone     : '',
			street    : '',
			city      : '',
			state     : '',
			timeZone  : '',
			zip       : '',
		};
	}

	componentDidMount() {
		const { user } = this.props;
		if (!isEmpty(user)) this.setFormState(user);
	}

	UNSAFE_componentWillReceiveProps = ({ user }) => {
		const lastProps = this.props;
		if (!isEqual(lastProps.user, user)) this.setFormState(user);
	}

	shouldComponentUpdate = ({
		user,
		errors,
		buttonState,
		isEdit,
		timeZones,
	}, {
		firstName,
		lastName,
		email,
		phone,
		street,
		city,
		state,
		timeZone,
		zip,
	}) => {
		const lastProps = this.props;
		const lastState = this.state;

		return (
			!isEqual(lastProps.user, user)
			|| !isEqual(lastProps.isEdit, isEdit)
			|| !isEqual(lastProps.errors, errors)
			|| !isEqual(lastProps.buttonState, buttonState)
			|| !isEqual(lastProps.timeZones, timeZones)
			|| !isEqual(lastState.firstName, firstName)
			|| !isEqual(lastState.lastName, lastName)
			|| !isEqual(lastState.phone, phone)
			|| !isEqual(lastState.email, email)
			|| !isEqual(lastState.street, street)
			|| !isEqual(lastState.city, city)
			|| !isEqual(lastState.state, state)
			|| !isEqual(lastState.timeZone, timeZone)
			|| !isEqual(lastState.zip, zip)
		);
	}

	componentWillUnmount = () => {
		const { resetForm } = this.props;
		resetForm(SETTINGS_TYPE);
	}

	setFormState = ({
		FirstName : firstName,
		LastName : lastName,
		CellPhone : phone,
		Email : email,
		StreetAddress : street,
		City : city,
		StateId : state,
		TimeZoneId: timeZone,
		Zip : zip,
	}) => {
		this.setState({
			firstName,
			lastName,
			phone,
			email,
			street,
			city,
			state,
			timeZone,
			zip,
		});
	}

	onCancel = () => {
		const { user, resetForm } = this.props;

		resetForm(SETTINGS_TYPE);
		this.setFormState(user);
	}

	onSave = (e) => {
		e.preventDefault();
		const { dispatch } = this.props;
		const { phone } = this.state;

		if (!this.validateForm()) {
			updateProgressButton({
				dispatch,
				module   : 'app',
				property : `${SETTINGS_TYPE}.buttonState`,
				value    : 'error',
			});
			return;
		}

		const data = {
			...this.state,
			phone : utils.getDigits(phone),
		};
		dispatch(thunks.updatePersonalSettings(data));
	}

	validateForm = () => {
		const { setErrors } = this.props;
		const data = cloneDeep(this.state);
		const errors = {};

		Object.keys(data).forEach((key) => {
			if (!data[key]) errors[key] = true;
		});

		setErrors(SETTINGS_TYPE, errors);
		return isEmpty(errors);
	}

	handleOnChange = (property) => (e) => {
		const { errors, setErrors } = this.props;
		const newState = cloneDeep(this.state);

		newState[property] = e.target ? e.target.value : e.value;

		this.setState(newState);

		if (errors[property]) delete errors[property];

		setErrors(SETTINGS_TYPE, errors);
	}

	getTimeZoneLabel = (timeZone) => {
		const { timeZones } = this.props;
		const idx = findIndex(timeZones, ['value', timeZone]);
		const label = get(timeZones[idx], ['label'], 'Loading...');

		return label;
	}

	render() {
		const {
			setRef,
			user,
			setEditMode,
			isEdit,
			errors,
			buttonState,
			USStates,
			timeZones,
		} = this.props;

		const {
			firstName,
			lastName,
			email,
			phone,
			street,
			city,
			state,
			timeZone,
			zip,
		} = this.state;

		const address = `${user.StreetAddress ? `${user.StreetAddress}, ` : ''} ${getCityStateZipString(user.City, user.StateName, user.Zip)}`;

		return (
			<div
				className="card-section"
				ref={(ref) => setRef(ref)}
			>
				<div className="flex">
					<h5 className="color-dark font-bold margin-bottom-20 flex-1">Personal Settings</h5>
					<If condition={!isEdit}>
						<Button
							bsStyle="link"
							onClick={() => setEditMode(SETTINGS_TYPE, true)}
						>
							<i className="fa fa-pencil margin-right-5" />
							{' '}
							Edit details
						</Button>
					</If>
				</div>
				<Choose>
					<When condition={isEdit}>
						<FormField
							title="First Name"
							placeholder="First Name"
							name="firstName"
							value={firstName}
							onChange={this.handleOnChange('firstName')}
							isError={errors.firstName}
							isEdit
							hasSeperator
						/>
						<FormField
							title="Last Name"
							placeholder="Last Name"
							name="lastName"
							value={lastName}
							onChange={this.handleOnChange('lastName')}
							isError={errors.lastName}
							isEdit
							hasSeperator
						/>
					</When>
					<Otherwise>
						<FormField
							title="Name"
							name="full-name"
							value={`${user.FirstName} ${user.LastName}`}
							hasSeperator
						/>
					</Otherwise>
				</Choose>
				<FormField
					title="Email"
					isEdit={isEdit}
					isError={errors.email}
					placeholder="Email"
					type="email"
					name="email"
					onChange={this.handleOnChange('email')}
					value={isEdit ? email : user.Email}
					disabled
					hasSeperator
				/>
				<FormField
					title="Phone"
					name="phone"
					isEdit={isEdit}
					isError={errors.phone}
					placeholder="Phone"
					inputMask={constants.MASK_VALUE.phoneMask}
					onChange={this.handleOnChange('phone')}
					value={isEdit ? phone : utils.formatPhoneNumber(user.CellPhone)}
					disabled
					hasSeperator
				/>
				<Choose>
					<When condition={isEdit}>
						<FormField
							title="Street"
							placeholder="Street"
							value={street}
							name="street"
							onChange={this.handleOnChange('street')}
							isError={errors.street}
							isEdit
							hasSeperator
						/>
						<FormField
							title="City"
							placeholder="City"
							value={city}
							name="city"
							onChange={this.handleOnChange('city')}
							isError={errors.city}
							isEdit
							hasSeperator
						/>
						<FormField
							title="State"
							addOn={(
								<div className="flex-3 flex">
									<div className="flex-1 margin-right-30">
										<FormGroup
											className="margin-bottom-0"
											validationState={errors.state ? 'error' : null}
										>
											<Select
												className="react-select settings-form-field"
												value={state}
												name="state"
												onChange={this.handleOnChange('state')}
												options={USStates}
											/>
										</FormGroup>
									</div>
									<div className="flex-1">
										<FormGroup
											className="margin-bottom-0"
											validationState={errors.zip ? 'error' : null}
										>
											<FormControl
												className="settings-form-field"
												value={zip}
												placeholder="Zip"
												name="zip"
												onChange={this.handleOnChange('zip')}
											/>
										</FormGroup>
									</div>
								</div>
							)}
							hasSeperator
						/>
						<FormField
							className="flex-1"
							title="Timezone"
							addOn={(
								<FormGroup
									className="flex-3 margin-bottom-0"
									validationState={errors.state ? 'error' : null}
								>
									<Select
										className="settings-form-field react-select"
										name="timeZone"
										value={timeZone}
										onChange={this.handleOnChange('timeZone')}
										options={timeZones}
									/>
								</FormGroup>
							)}
						/>
					</When>
					<Otherwise>
						<FormField
							title="Location"
							name="address"
							value={address}
							hasSeperator
						/>
						<FormField
							title="Timezone"
							name="timeZone"
							value={this.getTimeZoneLabel(timeZone)}
						/>
					</Otherwise>
				</Choose>
				<If condition={isEdit}>
					<div className="width-all text-right margin-top-15">
						<Button
							bsStyle="link"
							className="margin-right-10"
							onClick={this.onCancel}
						>
							Cancel
						</Button>
						<ProgressButton
							type="submit"
							className="btn btn-primary height-42"
							text="Save"
							onClick={this.onSave}
							state={buttonState}
						/>
					</div>
				</If>
			</div>
		);
	}
}

const mapStateToProps = ({
	app : {
		user,
		[SETTINGS_TYPE] : {
			errors,
			buttonState,
			isEdit,
		},
		USStates,
		timeZones,
	},
}) => ({
	user,
	errors,
	buttonState,
	isEdit,
	USStates,
	timeZones,
});

const mapDispatchToProps = (dispatch) => ({
	dispatch,
	resetForm   : bindActionCreators(Actions, dispatch).resetForm,
	setErrors   : bindActionCreators(Actions, dispatch).setFormErrors,
	setEditMode : bindActionCreators(Actions, dispatch).setEditMode,
});

PersonalSettings.propTypes = {
	USStates    : PropTypes.array,
	buttonState : PropTypes.string,
	dispatch    : PropTypes.func.isRequired,
	errors      : PropTypes.object.isRequired,
	isEdit      : PropTypes.bool,
	resetForm   : PropTypes.func.isRequired,
	setEditMode : PropTypes.func.isRequired,
	setErrors   : PropTypes.func.isRequired,
	setRef      : PropTypes.func.isRequired,
	timeZones   : PropTypes.array,
	user        : PropTypes.object,
};

PersonalSettings.defaultProps = {
	USStates    : [],
	buttonState : '',
	isEdit      : false,
	timeZones   : [],
	user        : {},
};

export default connect(mapStateToProps, mapDispatchToProps)(PersonalSettings);
